<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Pacjenci</span>
        <h3 class="page-title">
          <template v-if="isEdit">
            Edycja pacjenta:
            <p class="mt-2" style="font-weight: 100">
            {{data.name + ' ' + data.last_name}}
            <span
              v-if="data.patient_number"
              style="opacity: 0.5"
              class="text-light">
              #{{data.patient_number}}
            </span>
          </p>
          </template>
          <template v-else>Dodawanie pacjenta</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/pacjenci')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          v-if="isEdit"
          theme="info"
          class="mr-1"
          @click.prevent="validateForm(false)">
          Zastosuj zmiany
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm(true)">
          Zapisz i wyjdź
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <div
          v-if="!allDataLoaded && !loadError"
          class="loading-spinner pb-3 pt-3 text-center">
          <span
            class="pb-3 pt-3 text-center overlay-loading"
            style="z-index: 100;">
            Trwa ładowanie danych&hellip;
          </span>
        </div>
        <template v-if="!loadError">
          <d-tabs
            ref="patients-form-tabs"
            card
            :nav-class="allDataLoaded ? validationResults : ''"
            :class="{'is-loading': !allDataLoaded}">
            <!-- Historia Wizyt -->
            <d-tab
              v-if="isEdit"
              title="Historia Wizyt">
              <patients-form-visits
                v-if="allDataLoaded"
                ref="patients-form-visits"
                :all-institutions="allInstitutions"
                :all-users="allUsers"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 0 : true"
                :patient="data" />
            </d-tab>
            <!-- Dane dziecka -->
            <d-tab title="Dane dziecka">
              <patients-form-general
                v-if="allDataLoaded"
                ref="patients-form-general"
                :all-institutions="allInstitutions"
                :is-edit="isEdit"
                :is-visible="true"
                :patient="data" />
            </d-tab>
            <!-- Dane opiekunów -->
            <d-tab
              v-if="isEdit"
              title="Dane opiekunów">
              <patients-form-guardians
                v-if="allDataLoaded"
                ref="patients-form-guardians"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 2 : true"
                :patient="data" />
            </d-tab>
            <!-- E-dokumenty -->
            <d-tab
              v-if="isEdit"
              title="E-dokumenty">
              <patients-form-documents
                v-if="allDataLoaded"
                ref="patients-form-documents"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 3 : true"
                :patient="data" />
            </d-tab>
            <!-- Badania -->
            <d-tab
              v-if="isEdit"
              title="Badania">
              <patients-form-medical
                v-if="allDataLoaded"
                ref="patients-form-medical"
                :all-institutions="allInstitutions"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 4 : true"
                :patient="data" />
            </d-tab>
            <!-- Wywiad -->
            <d-tab
              v-if="isEdit"
              title="Wywiady">
              <patients-form-interviews
                v-if="allDataLoaded"
                ref="patients-form-interviews"
                :all-users="allUsers"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 5 : true"
                :patient="data" />
            </d-tab>
            <!-- Wyniki diagnozy -->
            <d-tab
              v-if="isEdit"
              title="Wyniki diagnozy">
              <patients-form-diagnosis
                v-if="allDataLoaded"
                ref="patients-form-diagnosis"
                :all-users="allUsers"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 6 : true"
                :patient="data" />
            </d-tab>
            <!-- Informacje -->
            <d-tab
              v-if="isEdit"
              title="Informacje">
              <patients-form-information
                v-if="allDataLoaded"
                ref="patients-form-information"
                :all-users="allUsers"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 7 : true"
                :patient="data" />
            </d-tab>
            <!-- Opinie -->
            <d-tab
              v-if="isEdit"
              title="Opinie">
              <patients-form-opinions
                v-if="allDataLoaded"
                ref="patients-form-opinions"
                :all-institutions="allInstitutions"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 8 : true"
                :patient="data" />
            </d-tab>
            <!-- Orzeczenia -->
            <d-tab
              v-if="isEdit"
              title="Orzeczenia">
              <patients-form-decisions
                v-if="allDataLoaded"
                ref="patients-form-decisions"
                :all-institutions="allInstitutions"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 9 : true"
                :patient="data" />
            </d-tab>
            <!-- Upoważnienia -->
            <d-tab
              v-if="isEdit"
              title="Upoważnienia">
              <patients-form-auth
                v-if="allDataLoaded"
                ref="patients-form-auth"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 10 : true"
                :patient="data" />
            </d-tab>
            <!-- Lista oczekująca -->
            <d-tab
              v-if="isEdit"
              title="Lista oczekująca">
              <patients-form-reservations
                v-if="allDataLoaded"
                ref="patients-form-reservations"
                :all-institutions="allInstitutions"
                :is-visible="$refs['patients-form-tabs'] ? $refs['patients-form-tabs'].currentTab === 11 : true"
                :patient="data" />
            </d-tab>
          </d-tabs>
        </template>
        <template v-else>
          <d-alert
            show
            theme="warning"
            class="mt-3">
            Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
          </d-alert>
        </template>
      </d-card>
    </d-form>
    <visits-popup
      :close-modal="handleClosePopup"
      :show-modal="showModalVisit"
      :my-account="userData"
      :visit="selectedVisit || {}"/>
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import PatientsFormVisits from '@/components/forms/patients/PatientsFormVisits.vue';
import PatientsFormGeneral from '@/components/forms/patients/PatientsFormGeneral.vue';
import PatientsFormGuardians from '@/components/forms/patients/PatientsFormGuardians.vue';
import PatientsFormDocuments from '@/components/forms/patients/PatientsFormDocuments.vue';
import PatientsFormMedical from '@/components/forms/patients/PatientsFormMedical.vue';
import PatientsFormInterviews from '@/components/forms/patients/PatientsFormInterviews.vue';
import PatientsFormDiagnosis from '@/components/forms/patients/PatientsFormDiagnosis.vue';
import PatientsFormInformation from '@/components/forms/patients/PatientsFormInformation.vue';
import PatientsFormOpinions from '@/components/forms/patients/PatientsFormOpinions.vue';
import PatientsFormDecisions from '@/components/forms/patients/PatientsFormDecisions.vue';
import PatientsFormAuth from '@/components/forms/patients/PatientsFormAuth.vue';
import PatientsFormReservations from '@/components/forms/patients/PatientsFormReservations.vue';
import VisitsPopup from '@/views/visits/VisitsPopup.vue';

export default {
  name: 'patients-form',
  components: {
    'patients-form-visits': PatientsFormVisits,
    'patients-form-general': PatientsFormGeneral,
    'patients-form-guardians': PatientsFormGuardians,
    'patients-form-documents': PatientsFormDocuments,
    'patients-form-medical': PatientsFormMedical,
    'patients-form-interviews': PatientsFormInterviews,
    'patients-form-diagnosis': PatientsFormDiagnosis,
    'patients-form-information': PatientsFormInformation,
    'patients-form-opinions': PatientsFormOpinions,
    'patients-form-decisions': PatientsFormDecisions,
    'patients-form-auth': PatientsFormAuth,
    'patients-form-reservations': PatientsFormReservations,
    'visits-popup': VisitsPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.allInstitutionsLoaded && this.allUsersLoaded;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    userData () {
      return this.$store.getters.getAccount
    },
    validationResults() {
      if (!this.allDataLoaded) {
        return false;
      }

      if (!this.isValidationRequested) {
        return '';
      }

      // at this point the only form to validate is this.$refs['patients-form-general'].$refs['patients-form-general'] ('Dane dziecka' tab)
      // if validation observer woudl be for all tabs it would validate popups as well and we don't want that
      let rawErrors = this.$refs['patients-form-general'].$refs['patients-form-general'].errors;
      let errorKeys = Object.keys(rawErrors);
      let errors = [];

      for (let i = 0; i < errorKeys.length; i++) {
        if (rawErrors[errorKeys[i]].length) {
          errors.push(errorKeys[i]);
        }
      }

      let errorTabs = [];

      if (errors.find(item => item.startsWith('patient-'))) {
        errorTabs.push('error-2');
      }

      return errorTabs.join(' ');
    },
  },
  data() {
    return {
      data: {
        id: '',
        birthday_date: '',
        city: '',
        email: '',
        identification: null,
        institution_id: '',
        last_name: '',
        name: '',
        nfz: null,
        nip: null,
        number_home: '',
        number_local: '',
        patient_number: '',
        pesel: '',
        phone: '',
        place_of_birth: '',
        post_code: '',
        previous_id: '',
        school_city: '',
        school_class: '',
        school_name: '',
        school_postcode: '',
        school_street: '',
        school_street_local_number: '',
        school_street_number: '',
        school_type: '',
        sex: '',
        street: '',
        visits_history: [],
        historical_patient_numbers: []
      },
      loaded: false,
      loadError: false,
      allInstitutions: [],
      allInstitutionsLoaded: false,
      allUsers: [],
      allUsersLoaded: false,
      isValidationRequested: false,
      selectedVisit: null,
      shouldGoBackToList: true,
      showModalVisit: false
    };
  },
  mounted () {
    this.$bus.$on('patient-visit-show-popup', this.showVisitPopup);
    this.$bus.$on('submit-patient-form', this.validateForm);
    this.loadAdditionalData();
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/patients/item/',
        instanceFields: [
          'id',
          'birthday_date',
          'city',
          'email',
          'identification',
          'institution_id',
          'last_name',
          'name',
          'nfz',
          'nip',
          'number_home',
          'number_local',
          'patient_number',
          'pesel',
          'phone',
          'place_of_birth',
          'post_code',
          'previous_id',
          'school_city',
          'school_class',
          'school_name',
          'school_postcode',
          'school_street',
          'school_street_local_number',
          'school_street_number',
          'school_type',
          'sex',
          'street',
          'visits_history',
          'historical_patient_numbers',
        ],
        loadedKey: 'loaded'
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/institutions/items?ignore_users_filters=1',
        outputKey: 'allInstitutions',
        loadedKey: 'allInstitutionsLoaded',
        params: {
          pagination: false
        },
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items?ignore_users_filters=1',
        outputKey: 'allUsers',
        loadedKey: 'allUsersLoaded',
        params: {
          pagination: false,
        }
      });
    },
    validateForm (goBackToList = false) {
      Vue.set(this, 'isValidationRequested', true);
      Vue.set(this, 'shouldGoBackToList', goBackToList);
      FormUtils.validate(this.$refs['patients-form-general'].$refs['patients-form-general'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/patients/update/' + this.id : '/patients/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano pacjenta',
        successText: 'Wybrany pacjent został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja pacjenta nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          if (this.shouldGoBackToList) {
            this.$router.push('/pacjenci');
            return;
          }
          this.loadItemData();
        }
      });
    },
    prepareFormDataToSend () {
      let newPatientData = this.$refs['patients-form-general'].getPatientData();
      let formDataToSend = {
        birthday_date: newPatientData.birthday_date,
        city: newPatientData.city,
        email: newPatientData.email,
        identification: this.data.identification,
        institution_id: newPatientData.institution_id,
        last_name: newPatientData.last_name,
        name: newPatientData.name,
        nfz: this.data.nfz,
        nip: this.data.nip,
        number_home: newPatientData.number_home,
        number_local: newPatientData.number_local,
        pesel: newPatientData.pesel,
        phone: newPatientData.phone,
        place_of_birth: newPatientData.place_of_birth,
        post_code: newPatientData.post_code,
        previous_id: newPatientData.previous_id,
        school_city: newPatientData.school_city,
        school_class: newPatientData.school_class,
        school_name: newPatientData.school_name,
        school_postcode: newPatientData.school_postcode,
        school_street: newPatientData.school_street,
        school_street_local_number: newPatientData.school_street_local_number,
        school_street_number: newPatientData.school_street_number,
        school_type: newPatientData.school_type,
        sex: newPatientData.sex,
        street: newPatientData.street
      };
      return formDataToSend;
    },
    loadVisitData (visitID) {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/visits/list',
        outputKey: 'visitItem',
        pagination: false,
        params: {
          pagination: false,
          visit_id: visitID,
        },
        successAction: (response) => {
          if (response.length && response.length === 1) {
            Vue.set(this, 'selectedVisit', response[0]);
            Vue.nextTick(() => {
              Vue.set(this, 'showModalVisit', true);
            })
          } else {
            console.warn('wrong visit reponse');
          }
        }
      });
    },
    showVisitPopup (visitID) {
      this.loadVisitData(visitID);
    },
    handleClosePopup() {
      Vue.set(this, 'showModalVisit', false);
      Vue.set(this, 'selectedVisit', null);
    },
  },
  beforeDestroy () {
    this.$bus.$off('patient-visit-show-popup', this.showVisitPopup);
    this.$bus.$off('submit-patient-form', this.validateForm);
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/variables/colors";
  .healthAndMedical {
    p {
      font-size: 0.9rem;
      margin-bottom: 0;
      font-weight: 100;
      span {
        font-weight: 700;
      }
    }
  }
  .mx-input {
    padding: 17px 30px;
  }
  .term {
    text-align: center;
    margin: 1rem 0;
    font-size: 2rem;
    color: #c4183c
  }
  .status-available {
    color: #28a745;
  }
  .status-missing {
    color: #6c757d;
    opacity: 0.6;
  }
  .modal-dialog {
    margin-top: 8vh;
  }

  .patient-name {
    font-weight: 100;
    color: $data-green;
  }
</style>
